/**
 * useReelStore
 */
import { useCallback } from 'react';
import {
    Queries,
    ApolloLib,
    Mutations,
} from 'bdvp-graphql';

const {
    useMutation,
    useQuery,
    useApolloClient,
} = ApolloLib;
const {
    GET_REEL,
    GET_REEL_CLIP_TRIMMING_MEDIA_ID,
    VALIDATE_REEL,
} = Queries;
const {
    SET_REEL,
    RESET_REEL,
    ADD_REEL_CLIP,
    REMOVE_REEL_CLIP,
    REORDER_REEL_CLIP,
    SET_REEL_CLIP_TRIMMING_MEDIA_ID,
    UPDATE_REEL_CLIP,
    CREATE_REEL,
    DELETE_REEL,
} = Mutations;

export const REEL_STORAGE_ITEM = 'reel';

export const updateLocalReel = (data) => {

    // Mutation returns null if clip is already in the reel
    if (!data) {

        return;

    }

    try {

        sessionStorage.setItem(
            REEL_STORAGE_ITEM,
            JSON.stringify({
                timestamp: Date.now(),
                data,
            })
        );

    } catch (e) {

        if (
            [
                'QuotaExceededError',
                'NS_ERROR_DOM_QUOTA_REACHED',
            ].includes(
                e.name
            )
        ) {
            // handle quota limit exceeded error
            // possibly message to user
        }

        console.log(e);

    }

};

export const getLocalReel = () => {

    if (typeof (sessionStorage) === "undefined") {

        return;

    }

    return JSON.parse(sessionStorage.getItem(REEL_STORAGE_ITEM));

};

export const removeLocalReel = () => {

    sessionStorage.removeItem(REEL_STORAGE_ITEM);

};

const getAccessToken = () => {

    return typeof mlbOkta !== 'undefined' ? mlbOkta.getAuthClient().tokenManager.get('access_token').accessToken : '';

};

const useReelStore = () => {

    const client = useApolloClient();

    const { data } = useQuery(GET_REEL);

    const { data: trimmingData } = useQuery(GET_REEL_CLIP_TRIMMING_MEDIA_ID, {
        fetchPolicy: 'cache-only',
    });

    const [setReel] = useMutation(SET_REEL);

    const [resetReel] = useMutation(RESET_REEL, {
        onCompleted: () => removeLocalReel(),
    });

    const [addReelClip] = useMutation(ADD_REEL_CLIP, {
        onCompleted: ({ addReelClip: data }) => updateLocalReel(data),
    });

    const [removeReelClip] = useMutation(REMOVE_REEL_CLIP, {
        onCompleted: ({ removeReelClip: data }) => updateLocalReel(data),
    });

    const [reorderReelClip] = useMutation(REORDER_REEL_CLIP, {
        onCompleted: ({ reorderReelClip: data }) => updateLocalReel(data),
    });

    const [setReelClipTrimmingMediaId] = useMutation(SET_REEL_CLIP_TRIMMING_MEDIA_ID);

    const [updateReelClip] = useMutation(UPDATE_REEL_CLIP, {
        onCompleted: ({ updateReelClip: data }) => updateLocalReel(data),
    });

    const [
        createReel,
        {
            loading: createReelLoading,
            data: createReelData,
            error: createReelError ,
        },
    ] = useMutation(CREATE_REEL, {
        onCompleted: ({ addReel: data }) => {

            if (data.videoUrl) {

                resetReel();

            }

        },
    });

    const [
        deleteReel,
        {
            loading: deleteReelLoading,
        },

    ] = useMutation(DELETE_REEL, {
        refetchQueries: ['getReels'],
        awaitRefetchQueries: true,
    });

    const bootstrapReel = useCallback(() => {

        const reel = getLocalReel();

        if (!reel || !reel.data) {

            return;

        }

        return setReel({
            variables: {
                reel: reel.data,
            },
        });

    }, [setReel]);

    const _addReelClip = useCallback(({
        mediaId,
        title,
        clipSlug,
        feedType,
        thumbnailUrl,
        originalDuration,
    }) => addReelClip({
        variables: {
            mediaId,
            title,
            clipSlug,
            feedType,
            thumbnailUrl,
            originalDuration,
        },
    }), [addReelClip]);

    const _removeReelClip = useCallback((
        mediaId
    ) => removeReelClip({
        variables: {
            mediaId,
        },
    }), [removeReelClip]);

    const _reorderReelClip = useCallback((
        startIndex,
        endIndex
    ) => reorderReelClip({
        variables: {
            startIndex,
            endIndex,
        },
    }), [reorderReelClip]);

    const _setReelClipTrimmingMediaId = useCallback((
        mediaId
    ) => setReelClipTrimmingMediaId({
        variables: {
            mediaId,
        },
    }), [setReelClipTrimmingMediaId]);

    const _updateReelClip = useCallback((
        mediaId,
        offsetStartTime,
        offsetEndTime
    ) => updateReelClip({
        variables: {
            mediaId,
            offsetStartTime,
            offsetEndTime,
        },
    }), [updateReelClip]);

    const validateReel = useCallback(async(reelRequest) => {

        const { data } = await client.query({
            query: VALIDATE_REEL,
            variables: { reelRequest },
        });
        return data.validateReel;

    }, [client]);

    const _createReel = useCallback((
        reelRequest
    ) => {

        const token = getAccessToken();

        createReel({
            variables: {
                reelRequest,
            },
            context: {
                headers: {
                    authorization: token ? `Bearer ${token}` : '',
                },
            },
        });

    }, [createReel]);

    const _deleteReel = useCallback((
        reelId
    ) => {

        const token = getAccessToken();

        deleteReel({
            variables: {
                reelId,
            },
            context: {
                headers: {
                    authorization: token ? `Bearer ${token}` : '',
                },
            },
        });

    }, [deleteReel]);

    return {
        data,
        bootstrapReel,
        addReelClip: _addReelClip,
        removeReelClip: _removeReelClip,
        reorderReelClip: _reorderReelClip,
        updateReelClip: _updateReelClip,
        trimmingMediaId: trimmingData?.trimmingMediaId,
        setTrimmingMediaId: _setReelClipTrimmingMediaId,
        validateReel,
        createReel: _createReel,
        deleteReel: _deleteReel,
        createReelLoading,
        createReelData,
        createReelError,
        deleteReelLoading,
    };

};

export default useReelStore;
